import { DiscordIcon } from "@/components/DiscordIcon";
import { GchatIcon } from "@/components/GchatIcon";
import { SlackIcon } from "@/components/SlackIcon";
import { TeamsIcon } from "@/components/TeamsIcon";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { ChannelPlatform, PersonHandle } from "@/lib/api";
import { type DashboardLevel, type LevelKey, levelLabel, type LevelOption } from "@/lib/people-levels";
import { cn } from "@/lib/utils";
import { Check, ChevronDown, Loader2, Mail, Plus, X } from "lucide-react";
import { useId, useState } from "react";
import { PLATFORM_LABEL } from "../channel-mode";
import { levelDot } from "./person-format";

const PLATFORM_ICON: Partial<Record<ChannelPlatform, (p: { className?: string }) => React.ReactNode>> = {
  slack: SlackIcon,
  teams: TeamsIcon,
  gchat: GchatIcon,
  discord: DiscordIcon,
};

export function PlatformIcons({ handles }: { handles: PersonHandle[] }) {
  const providers = [...new Set(handles.map((h) => h.provider))].filter((p) => PLATFORM_ICON[p]);
  if (providers.length === 0) return null;
  return (
    <span className="inline-flex shrink-0 items-center gap-1">
      {providers.map((p) => {
        const Icon = PLATFORM_ICON[p];
        return (
          <span key={p} title={PLATFORM_LABEL[p]} className="inline-flex">
            {Icon && <Icon className="size-3.5" />}
            <span className="sr-only">{PLATFORM_LABEL[p]}</span>
          </span>
        );
      })}
    </span>
  );
}

export function LevelPicker({
  value,
  options,
  editable,
  busy,
  personName,
  onPick,
  onInviteAsAdmin,
}: {
  value: LevelKey;
  options: LevelOption[];
  editable: boolean;
  busy: boolean;
  personName: string;
  onPick: (key: LevelKey) => void;
  onInviteAsAdmin?: () => void;
}) {
  const label = levelLabel(value);
  if (!editable) {
    return (
      <span className="inline-flex min-h-8 max-w-52 items-center gap-2 px-1 text-sm text-muted-foreground">
        <span className={cn("size-1.5 shrink-0 rounded-full", levelDot(value))} />
        <span className="line-clamp-2 break-words">{label}</span>
      </span>
    );
  }
  const main = options.filter((o) => o.key !== "blocked");
  const blocked = options.find((o) => o.key === "blocked");
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild disabled={busy}>
        <Button
          variant="outline"
          size="sm"
          className="h-auto min-h-8 w-full max-w-64 justify-between sm:max-w-52 gap-2 py-1.5 text-left font-normal whitespace-normal"
          aria-label={`Access level for ${personName}: ${label}`}
        >
          <span className="flex min-w-0 items-center gap-2">
            {busy ? (
              <Loader2 className="size-3 shrink-0 animate-spin text-muted-foreground" />
            ) : (
              <span className={cn("size-1.5 shrink-0 rounded-full", levelDot(value))} />
            )}
            <span className="line-clamp-2 break-words">{label}</span>
          </span>
          <ChevronDown className="size-3.5 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="max-h-[min(28rem,var(--radix-dropdown-menu-content-available-height))] w-72 overflow-y-auto"
      >
        {main.map((o) => (
          <LevelItem key={o.key} option={o} selected={o.key === value} onSelect={() => onPick(o.key)} />
        ))}
        {blocked && (
          <>
            <DropdownMenuSeparator />
            <LevelItem option={blocked} selected={value === "blocked"} onSelect={() => onPick("blocked")} />
          </>
        )}
        {onInviteAsAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onInviteAsAdmin} className="gap-2 py-2">
              <Mail className="size-4 shrink-0 text-muted-foreground" />
              <span className="text-sm">Invite to the dashboard as an admin</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function LevelItem({
  option,
  selected,
  onSelect,
}: {
  option: LevelOption;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <DropdownMenuItem onSelect={onSelect} className="items-start gap-2 py-2">
      <span className={cn("mt-1.5 size-1.5 shrink-0 rounded-full", levelDot(option.key))} />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="flex items-start gap-2 text-sm">
          <span className="break-words">{option.label}</span>
          {selected && <Check className="ml-auto size-3.5 shrink-0 text-foreground/70" />}
        </span>
        {option.hint && <span className="text-xs leading-snug text-muted-foreground whitespace-normal">{option.hint}</span>}
      </div>
    </DropdownMenuItem>
  );
}

export function LevelRadioGroup({
  value,
  options,
  onChange,
}: {
  value: DashboardLevel;
  options: LevelOption[];
  onChange: (key: DashboardLevel) => void;
}) {
  const id = useId();
  return (
    <RadioGroup value={value} onValueChange={(v) => onChange(v as DashboardLevel)} className="gap-2">
      {options.map((o) => (
        <Label
          key={o.key}
          htmlFor={`${id}-${o.key}`}
          className={cn(
            "flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 font-normal",
            value === o.key && "border-foreground/30 bg-muted/40",
          )}
        >
          <RadioGroupItem id={`${id}-${o.key}`} value={o.key} className="mt-0.5" />
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="flex items-center gap-2 text-sm">
              <span className={cn("size-1.5 shrink-0 rounded-full", levelDot(o.key))} />
              {o.label}
            </span>
            <span className="text-xs leading-snug text-muted-foreground">{o.hint}</span>
          </span>
        </Label>
      ))}
    </RadioGroup>
  );
}

export function GroupChips({
  value,
  kept,
  all,
  editable,
  personName,
  onAdd,
  onRemove,
}: {
  value: string[];
  kept: string[];
  all: string[];
  editable: boolean;
  personName: string;
  onAdd: (group: string) => void;
  onRemove: (group: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const keptOnly = kept.filter((g) => !value.includes(g));
  const addable = all.filter((g) => !value.includes(g) && !keptOnly.includes(g));
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1">
      {value.map((g) => (
        <span key={g} className="inline-flex max-w-full items-center gap-1 rounded-md bg-muted py-0.5 pl-2 pr-1 text-xs font-medium">
          <span className="truncate" title={g}>
            {g}
          </span>
          {editable && (
            <button
              type="button"
              onClick={() => onRemove(g)}
              aria-label={`Remove ${personName} from ${g}`}
              className="relative rounded-sm p-0.5 text-muted-foreground hover:bg-background hover:text-foreground after:absolute after:-inset-2"
            >
              <X className="size-3" />
            </button>
          )}
        </span>
      ))}
      {keptOnly.map((g) => (
        <span
          key={`kept:${g}`}
          className="inline-flex max-w-full items-center gap-1 rounded-md border border-dashed px-2 py-0.5 text-xs font-medium"
        >
          <span className="truncate" title={g}>
            {g}
          </span>
          <span className="shrink-0 font-normal text-muted-foreground">kept from before</span>
        </span>
      ))}
      {editable && addable.length > 0 && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              size="xs"
              variant="ghost"
              className={cn("text-muted-foreground", value.length + keptOnly.length === 0 && "-ml-2")}
              aria-label={`Add ${personName} to a group`}
            >
              <Plus />
              Group
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0" align="start">
            <Command filter={(v, q) => (v.toLowerCase().includes(q.trim().toLowerCase()) ? 1 : 0)}>
              {addable.length > 8 && <CommandInput placeholder="Find group" />}
              <CommandList className="max-h-72">
                <CommandEmpty>No group matches.</CommandEmpty>
                <CommandGroup>
                  {addable.map((g) => (
                    <CommandItem
                      key={g}
                      value={g}
                      onSelect={() => {
                        setOpen(false);
                        onAdd(g);
                      }}
                    >
                      <span className="truncate">{g}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
